import React, { useState } from 'react';
import { MARKET_PRICES, DERIVED_RATES, getNIcpNavOverIcp } from '../constants/pricing';

const UnstakingModal = ({ isOpen, onClose, userNICP = 0 }) => {
  const [unstakeAmount, setUnstakeAmount] = useState(100);
  const [selectedOption, setSelectedOption] = useState('market'); // 'market' or 'waterneuron'

  const mockData = {
    icpPrice: MARKET_PRICES.ICP,
    nIcpMarketPrice: MARKET_PRICES.nICP,
    nIcpToIcp_Market: DERIVED_RATES.nIcpToIcp_Market,          // sell nICP → ICP (market rate)
    nIcpToIcp_Redemption: getNIcpNavOverIcp(),                 // redeem nICP → ICP (protocol rate)
    unstakingPeriod: 6,                                        // months
  };

  if (!isOpen) return null;

  // Calculate unstaking options
  const calculateUnstaking = (nIcpAmount) => {
    const market = {
      icpReceived: nIcpAmount * mockData.nIcpToIcp_Market,
      type: 'Instant Market Swap',
      description: 'Sell nICP on DEX for immediate liquidity',
      waitTime: 'Instant',
      rate: mockData.nIcpToIcp_Market,
      tradeName: 'Market Rate'
    };

    const waterneuron = {
      icpReceived: nIcpAmount * mockData.nIcpToIcp_Redemption,
      type: 'Using WaterNeuron',
      description: 'Unstake through protocol for full value + rewards',
      waitTime: `${mockData.unstakingPeriod} months`,
      rate: mockData.nIcpToIcp_Redemption,
      tradeName: 'Protocol Rate'
    };

    return { market, waterneuron };
  };

  const options = calculateUnstaking(unstakeAmount);
  const maxAmount = userNICP;
  const hasInsufficientFunds = unstakeAmount > maxAmount;

  return (
    <div className="modal-overlay">
      <div className="unstaking-modal">
        <div className="unstaking-header">
          <div className="modal-title-section">
            <h2 className="modal-title">Unstake nICP</h2>
            <p className="modal-subtitle">Choose your unstaking method</p>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="unstaking-body">
          {/* Amount Input */}
          <div className="unstaking-input">
            <label className="input-label">Amount to unstake</label>
            <div className="input-container">
              <input
                type="number"
                value={unstakeAmount}
                onChange={(e) => setUnstakeAmount(Number(e.target.value))}
                className="amount-input"
                min="0"
                max={maxAmount}
                step="0.1"
              />
              <div className="input-actions">
                <button 
                  className="max-button"
                  onClick={() => setUnstakeAmount(maxAmount)}
                >
                  MAX
                </button>
                <span className="input-suffix">nICP</span>
                <div className="token-icon">💧</div>
              </div>
            </div>
            
            {hasInsufficientFunds && (
              <div className="error-message">
                ⚠️ You don't have enough funds to complete the transaction.
              </div>
            )}
          </div>

          {/* Unstaking Options */}
          <h4 className="options-title">Choose unstaking method</h4>
          <div className="unstaking-options">
            <div 
              className={`option-radio ${selectedOption === 'market' ? 'selected' : ''}`}
              onClick={() => setSelectedOption('market')}
            >
              <div className="radio-header">
                <input 
                  type="radio" 
                  name="unstakeMethod" 
                  value="market"
                  checked={selectedOption === 'market'}
                  onChange={() => setSelectedOption('market')}
                />
                <div className="option-info">
                  <h5>Instant Market Swap</h5>
                  <p className="option-subtitle">Sell on DEX for instant liquidity</p>
                </div>
                <div className="option-amount">
                  <span className="amount">{options.market.icpReceived.toFixed(2)} ICP</span>
                  <span className="wait-time">Instant</span>
                </div>
              </div>
            </div>

            <div 
              className={`option-radio ${selectedOption === 'waterneuron' ? 'selected' : ''}`}
              onClick={() => setSelectedOption('waterneuron')}
            >
              <div className="radio-header">
                <input 
                  type="radio" 
                  name="unstakeMethod" 
                  value="waterneuron"
                  checked={selectedOption === 'waterneuron'}
                  onChange={() => setSelectedOption('waterneuron')}
                />
                <div className="option-info">
                  <h5>Ustake via WaterNeuron (6 months)</h5>
                  <p className="option-subtitle">Protocol unstake with full value + rewards</p>
                </div>
                <div className="option-amount">
                  <span className="amount">{options.waterneuron.icpReceived.toFixed(2)} ICP</span>
                  <span className="wait-time">{options.waterneuron.waitTime}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="unstaking-summary">
            <div className="summary-item">
              <span className="summary-label">You will receive:</span>
              <span className="summary-value">
                {selectedOption === 'market' 
                  ? `${options.market.icpReceived.toFixed(2)} ICP (instant)`
                  : `${options.waterneuron.icpReceived.toFixed(2)} ICP (in ${mockData.unstakingPeriod} months)`
                }
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Method:</span>
              <span className="summary-value">
                {selectedOption === 'market' ? 'Market swap' : 'WaterNeuron protocol'}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="unstaking-actions">
          <button 
            className="action-button secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className={`action-button primary ${selectedOption === 'market' ? 'market-btn' : 'waterneuron-btn'}`}
            disabled={hasInsufficientFunds}
            onClick={() => {
              // Handle unstaking action based on selectedOption
              alert(`Unstaking ${unstakeAmount} nICP via ${selectedOption === 'market' ? 'Market swap (ICP Swap)' : 'WaterNeuron'}`);
              onClose();
            }}
          >
            {selectedOption === 'market' 
              ? `Swap ${unstakeAmount} nICP` 
              : `Unstake via WaterNeuron`
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnstakingModal;
